'use client';

import {
	BigNum,
	BN,
	calculateLongShortFundingRate,
	convertToNumber,
	PRICE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import { useCallback, useEffect, useState } from 'react';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import useDriftClientIsReady from './useDriftClientIsReady';
import useUiUpdateInterval from './useUiUpdateInterval';
import { MarketId, UIMarket } from '@velocity-exchange/common';
import { usePerpMarketsLookup } from './perpMarkets/usePerpMarketsLookup';
import { useAllPricingData } from 'src/hooks/query/pricing/usePricingData';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

type MarketFundingRate = {
	marketIndex: number;
	fundingRate: number;
};

const areFundingsEqual = (a: MarketFundingRate[], b: MarketFundingRate[]) => {
	if (a.length !== b.length) return false;

	for (let i = 0; i < a.length; i++) {
		if (
			a[i].marketIndex !== b[i].marketIndex ||
			a[i].fundingRate !== b[i].fundingRate
		)
			return false;
	}

	return true;
};

/**
 * Uses trading history from the history server to calculate price change
 * @returns
 */
const usePredictedFunding = () => {
	const driftClientIsReady = useDriftClientIsReady();
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const setStore = useDriftStore((s) => s.set);
	const { data: allPricingData } = useAllPricingData();
	const allPricingDataRef = useSyncedRef(allPricingData);

	const [currentPredictedFunding, setCurrentPredictedFunding] = useState<
		MarketFundingRate[]
	>([]);
	const perpMarketsLookup = usePerpMarketsLookup();

	const updatePredictedFunding = useCallback(() => {
		if (!driftClientIsReady || !driftClient) return;

		const fundings: MarketFundingRate[] = [];

		perpMarketsLookup.forEach((market) => {
			const marketId = MarketId.createPerpMarket(market.marketIndex);
			const pricingData = allPricingDataRef.current?.get(marketId.key);

			// Skip if we don't have the necessary data
			if (!pricingData || pricingData.markPrice <= 0) return;

			const currentMarkPrice = BigNum.fromPrint(
				pricingData.markPrice.toString(),
				PRICE_PRECISION_EXP
			);
			// calculateLongShortFundingRate only uses oraclePriceData.price internally,
			// so we can construct a minimal object from the pricing query cache.
			const oraclePriceData = {
				price: BigNum.fromPrint(
					pricingData.oraclePrice.toString(),
					PRICE_PRECISION_EXP
				).val,
				slot: new BN(0),
				confidence: new BN(0),
				hasSufficientNumberOfDataPoints: true,
			};

			const isPredictionMarket = UIMarket.checkIsPredictionMarket(
				perpMarketsLookup[marketId.marketIndex]
			);

			// Skip if it's a prediction market because they don't have funding. ALSO if we change this later : There has been a bug observed where when a prediction market is resolved the oracle price inside calculateLongShortFundingRate is zero and creates a divide by zero error, make sure that is handled if we change this logic.
			if (isPredictionMarket) return;

			const perpMarketAccount = driftClient.getPerpMarketAccount(
				marketId.marketIndex
			);

			if (!perpMarketAccount) return;

			const nowBN = new BN((Date.now() / 1000).toFixed(0));

			let fundingInfo = [new BN(0), new BN(0)];
			try {
				fundingInfo = calculateLongShortFundingRate(
					perpMarketAccount,
					undefined,
					oraclePriceData,
					currentMarkPrice.val,
					nowBN
				);
			} catch {
				// noop
			}
			const longFundingRate = fundingInfo[0] ?? new BN(0);
			const shortFundingRate = fundingInfo[1] ?? new BN(0);

			const nextFundingRateNum = convertToNumber(
				longFundingRate.abs().gt(shortFundingRate.abs())
					? longFundingRate
					: shortFundingRate
			);

			fundings.push({
				marketIndex: marketId.marketIndex,
				fundingRate: nextFundingRateNum,
			});
		});

		if (!areFundingsEqual(fundings, currentPredictedFunding)) {
			setCurrentPredictedFunding(fundings);
		}
	}, [driftClient, driftClientIsReady]);

	// update price change in store when local value changes
	useEffect(() => {
		setStore((s) => {
			s.predictedFundings = currentPredictedFunding;
		});
	}, [currentPredictedFunding]);

	// update price change
	useUiUpdateInterval(updatePredictedFunding, true, true);
};

export default usePredictedFunding;
